// features/plans/containers/PlansContainer.tsx
"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PlansCard } from "../components/PlansCard";
import { plansData } from "../constants/plans";
import { PlansCart } from "../components/PlansCart";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCartStore } from "../store/useCartStore";
import { FloatingCartButton } from "../components/Cart";
import Navbar from "@/components/Common/Navbar";

export function PlansContainer() {
  const [selectedToggle, setSelectedToggle] = useState("keyword");
  const { isOpen, setIsOpen, getItemCount } = useCartStore();
  const itemCount = getItemCount();

  const toggles = [
    { value: "keyword", label: "Keyword" },
    { value: "influencer", label: "Influencer" },
    { value: "source", label: "Source" },
    { value: "pulse", label: "Pulse" },
  ];

  const selectedData = plansData[selectedToggle as keyof typeof plansData];
  const { subtitle, cards } = selectedData || { subtitle: "", cards: [] };

  return (
    <div className="h-screen p-3 pr-0 pb-3">
      <div
        className="scrollbar-thin relative h-full overflow-y-auto"
        style={{
          transform: "translateZ(0)",
          scrollBehavior: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div className="flex min-h-full gap-4">
          {/* Sidebar */}
          <div className="sticky top-0 h-fit w-[300px] flex-shrink-0 px-3">
            <Navbar subtitle="manager" />
          </div>

          {/* Main content */}
          <div className="flex flex-1 flex-col items-center justify-center gap-5 py-8 pr-8 md:ml-0 xl:-ml-[200px] 2xl:-ml-[300px]">
            {/* Toggles + subtitle */}
            <div className="flex flex-col items-center justify-center gap-4">
              <ToggleGroup
                type="single"
                className="flex-wrap gap-2"
                value={selectedToggle}
                onValueChange={(value) => {
                  if (value) {
                    setSelectedToggle(value);
                  }
                }}
              >
                {toggles.map(({ value, label }) => (
                  <ToggleGroupItem
                    key={value}
                    value={value}
                    className={cn(
                      "text-md h-[40px] w-[128px] rounded-[8px] p-2 font-bold tracking-normal text-neutral-800",
                      "transition-all duration-300 ease-in-out",
                      "hover:-translate-y-[1px] hover:bg-white/80 hover:shadow-xs",
                      selectedToggle === value
                        ? "scale-[1.03] bg-white shadow-sm"
                        : "bg-transparent",
                    )}
                  >
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>

              <p className="max-w-[700px] px-4 text-center text-sm text-neutral-700">
                {subtitle}
              </p>
            </div>

            {/* Cards */}
            <div className="w-full">
              <div className="flex flex-wrap items-end justify-center gap-6 py-2 transition-all duration-300 ease-in-out">
                {cards.map(
                  (
                    card: {
                      title: string;
                      price: string;
                      included: string[];
                      hasFreeTrial?: boolean;
                    },
                    index: number,
                  ) => (
                    <PlansCard
                      key={index}
                      title={card.title}
                      price={card.price}
                      included={card.included}
                      hasFreeTrial={card.hasFreeTrial}
                      isPopular={card.title === "Basic"}
                      monitoringType={selectedToggle}
                    />
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Cart Button with Sheet */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <FloatingCartButton itemCount={itemCount} />
        <SheetContent className="bg-white">
          <PlansCart />
        </SheetContent>
      </Sheet>
    </div>
  );
}
